import type { WhatsappConnectResult, WhatsappStatusResult } from '@barbeiropro/contracts';

export const STATUS_EVERY_MS = 3_000;
export const QR_EVERY_MS = 20_000;
export const POLL_MAX_MS = 5 * 60_000;

export interface ConnectPollingOptions {
  requestQr: () => Promise<WhatsappConnectResult>;
  checkStatus: () => Promise<WhatsappStatusResult>;
  onQr: (r: WhatsappConnectResult) => void;
  onConnected: (r: WhatsappStatusResult) => void;
  onTimeout?: () => void;
  onError?: (e: unknown) => void;
  statusEveryMs?: number;
  qrEveryMs?: number;
  maxMs?: number;
}

/**
 * Pede o QR na hora, renova a cada ~20 s e consulta o status a cada 3 s até conectar.
 * Nunca sobrepõe chamadas do mesmo tipo. Devolve a função que para tudo.
 */
export function startConnectPolling(opts: ConnectPollingOptions): () => void {
  let stopped = false;
  let qrBusy = false;
  let statusBusy = false;

  const stop = () => {
    stopped = true;
    clearInterval(statusTimer);
    clearInterval(qrTimer);
    clearTimeout(maxTimer);
  };

  const refreshQr = async () => {
    if (stopped || qrBusy) return;
    qrBusy = true;
    try {
      const r = await opts.requestQr();
      if (stopped) return;
      if (r.state === 'open') {
        const s = await opts.checkStatus();
        if (!stopped && s.status === 'connected') {
          stop();
          opts.onConnected(s);
        }
        return;
      }
      opts.onQr(r);
    } catch (e) {
      if (!stopped) opts.onError?.(e);
    } finally {
      qrBusy = false;
    }
  };

  const poll = async () => {
    if (stopped || statusBusy) return;
    statusBusy = true;
    try {
      const s = await opts.checkStatus();
      if (!stopped && s.status === 'connected') {
        stop();
        opts.onConnected(s);
      }
    } catch (e) {
      if (!stopped) opts.onError?.(e);
    } finally {
      statusBusy = false;
    }
  };

  const statusTimer = setInterval(poll, opts.statusEveryMs ?? STATUS_EVERY_MS);
  const qrTimer = setInterval(refreshQr, opts.qrEveryMs ?? QR_EVERY_MS);
  const maxTimer = setTimeout(() => {
    if (stopped) return;
    stop();
    opts.onTimeout?.();
  }, opts.maxMs ?? POLL_MAX_MS);
  void refreshQr();
  return stop;
}
